import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Actions as PENSIONACTIONS } from "../../../store/actions/pensionActions";
import {
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import EmployeePensionRow from "./EmployeePensionRow";
import Select from "react-select";
import { Link } from "react-router-dom";
import _ from "lodash";
import Tooltip from "@/common/tooltip/Tooltip";
import HeaderSort from "@/common/tables/HeaderSort";

const titles = [
	{ title: "Name", code: "name" },
	{ title: "Enrolment", code: ("status", "date") },
	{ title: "Group", code: "group.name" },
];

const EmployeePensions = ({
	getEmployeePensions,
	employees,
	groupOptions,
	statusOptions,
	pensionIntegrations,
	currency,
}) => {
	const [groupFilter, setGroupFilter] = useState();
	const [statusfilter, setStatusFilter] = useState();

	const [sortType, setSortType] = useState("asc");
	const [sortBy, setSortBy] = useState("name");

	const [search, setSearch] = useState("");
	const [itemsPerPage, setItemsPerPage] = useState(15);
	const [page, setPage] = useState(0);
	const start = page * itemsPerPage;
	const end = (page + 1) * itemsPerPage;
	const lastPage = Math.ceil(employees?.length / itemsPerPage);

	const Pagination = () => {
		if (employees?.length <= 0) return null;
		return (
			<thead>
				<tr>
					<th colSpan={8}>
						<div
							style={{
								display: "grid",
								gridGap: "1.5rem",
								grid: "1.5rem",
								gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
							}}
						>
							<div style={{ gridColumn: "span 1/ span 3", textAlign: "center" }}>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go To Page 1"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(0);
									}}
									disabled={page === 0}
								>
									<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go Back 5 Pages"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(Math.max(page - 5, 0));
									}}
									disabled={page === 0}
								>
									<ChevronDoubleLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-left"
									title="Go Back 1 Page"
									style={{ marginLeft: "0.5rem" }}
									onClick={() => {
										if (page != 0) setPage(page - 1);
									}}
									disabled={page === 0}
								>
									<ChevronLeftIcon style={{ height: "1.5em", float: "left" }} />
								</button>
							</div>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<strong>
									Page {page + 1}/{lastPage}
								</strong>
							</div>
							<div style={{ gridColumn: "span 1 / span 3", textAlign: "center" }}>
								<button
									className="btn btn-xs btn-default pull-right"
									style={{ marginRight: "0.5rem" }}
									title={`Go To Page ${lastPage}`}
									onClick={() => {
										if (page != lastPage - 1) setPage(lastPage - 1);
									}}
									disabled={page === lastPage - 1}
								>
									<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-right"
									style={{ marginRight: "0.5rem" }}
									title="Go Forward 5 Pages"
									onClick={() => {
										if (page != lastPage - 1) setPage(Math.min(page + 5, lastPage - 1));
									}}
									disabled={page === lastPage - 1}
								>
									<ChevronDoubleRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
								<button
									className="btn btn-xs btn-default pull-right"
									title="Go Forward 1 Page"
									onClick={() => {
										if (page !== lastPage - 1) setPage(page + 1);
									}}
									style={{ marginRight: "0.5rem" }}
									disabled={page === lastPage - 1}
								>
									<ChevronRightIcon style={{ height: "1.5em", float: "left" }} />
								</button>
							</div>
						</div>
					</th>
				</tr>
			</thead>
		);
	};

	useEffect(() => {
		getEmployeePensions();
	}, [itemsPerPage]);

	//Applies filters to employees
	const filteredEmployees = useMemo(() => {
		const filterByValue = (o) =>
			[
				"id",
				"name",
				"employeeName",
				"employeeSurname",
				"status",
				"group",
				"employeeContribution",
				"employerContribution",
			].some((k) => String(o[k])?.toLowerCase().includes(search.toLowerCase()));

		//Apply Search
		const filterResult = search.replace(/\s/g, "") ? employees?.filter(filterByValue) : employees;

		//Apply Filters
		return _.orderBy(
			filterResult?.filter(({ group, status }) => {
				const groupMatch = groupFilter ? group?.id == groupFilter : true;
				const statusMatch = statusfilter ? status == statusfilter : true;

				return groupMatch && statusMatch;
			}),
			[sortBy],
			[sortType],
		);
	}, [employees, search, groupFilter, statusfilter, sortBy, sortType]);

	return (
		<div className="mb-24">
			<div className="content">
				<div className="row">
					<div className="pull-left">
						<h5>Employee Pensions</h5>
					</div>
				</div>
				<div className="col-xs-12 p-0 mb-10">
					<div className="col-xs-3">
						<label>Group</label>
						<Select
							options={groupOptions}
							onChange={(e) => {
								setGroupFilter(e?.value);
							}}
							isClearable
						/>
					</div>
					<div className="col-xs-3">
						<label>Status</label>
						<Select
							options={statusOptions}
							onChange={(e) => {
								setStatusFilter(e?.value);
							}}
							isClearable
						/>
					</div>
				</div>
				<div className="col-xs-12">
					<div
						style={{
							border: "1px solid #bbbcbc",
							borderBottom: "none",
							position: "relative",
						}}
					>
						<MagnifyingGlassIcon
							className={"text-gray"}
							style={{
								height: "1.5em",
								position: "absolute",
								margin: "0.75em",
							}}
						></MagnifyingGlassIcon>
						<input
							name="search"
							onChange={(e) => setSearch(e.target.value)}
							value={search}
							style={{
								border: "none",
								display: "inline-block",
								maxWidth: "100%",
								width: "100%",
								padding: 10,
								paddingLeft: 40,
							}}
						/>
					</div>

					<div
						className="table-responsive"
						style={{ overflow: "auto" }}
					>
						<div className="dataTables_wrapper no-footer">
							<table className="js-table-sections table table-hover">
								<Pagination />
								<thead style={{ display: "table-header-group" }}>
									<tr>
										<th width="5%"></th>
										{titles.map((t) => (
											<HeaderSort
												title={t.title}
												code={t.code}
												sortBy={sortBy}
												setSortBy={setSortBy}
												sortType={sortType}
												setSortType={setSortType}
											/>
										))}
										<th style={{ userSelect: "none" }}>
											<span style={{ float: "left" }}>
												<Tooltip content="Employee Contribution">EE Contr.</Tooltip>
											</span>
										</th>
										<th style={{ userSelect: "none" }}>
											<span style={{ float: "left" }}>
												<Tooltip content="Employer Contribution">ER Contr.</Tooltip>
											</span>
										</th>
										{pensionIntegrations?.length > 0 && (
											<th style={{ textAlign: "right", userSelect: "none" }}>
												INTEGRATION LINKS
											</th>
										)}
										<th width="5%"></th>
									</tr>
								</thead>
								<tbody>
									{filteredEmployees?.slice(start, end)?.map((employee, index) => {
										const pensionPlan = employee?.pensionPlan;

										if (
											!pensionPlan &&
											(employee?.links?.length === 0 || pensionIntegrations?.length == 0)
										)
											return;

										return (
											<EmployeePensionRow
												name={employee?.name}
												occupation={pensionPlan?.occupation ?? employee?.occupations?.[0]}
												agreement={employee?.id}
												pensionPlan={pensionPlan}
												pensionIntegrations={pensionIntegrations}
												integrationLinks={employee?.links}
												status={employee?.status}
												group={employee?.group}
												currency={currency}
												enrolmentDate={employee?.date}
											/>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
			<div className="clearfix" />
		</div>
	);
};

const mapStateToProps = (state) => {
	const groups = state.pensions?.group?.list;

	const groupOptions = groups?.map((group) => ({ value: group.id, label: group.name }));
	groupOptions?.unshift({ label: "Custom Contributions", value: "custom" });

	const statusOptions = [
		{ value: "Enrolled", label: "Enrolled" },
		{ value: "Joined", label: "Joined" },
		{ value: "Postponed", label: "Postponed" },
		{ value: "Opted In", label: "Opted In" },
		{ value: "Opted Out", label: "Opted Out" },
		{ value: "Assessed", label: "Assessed" },
	];

	const employees =
		state.pensions?.employees &&
		state.pensions?.employees.map((e) => {
			const employeePensionPlan = [...e?.pensionPlans].pop();
			const group = groups?.find((group) => group.id === employeePensionPlan?.pensionGroup);

			const pensionPlan = (employeePensionPlan?.customPensionContribution && employeePensionPlan) || group;

			const enrolmentDeferredUntil = e?.enrolmentDeferredUntil;

			const statusCode = pensionPlan?.enrolmentBracketCode
				? pensionPlan?.enrolmentBracketCode?.split("-").pop() + "ed"
				: null;

			let date = pensionPlan?.startDate || pensionPlan?.start;
			let status = `Not Assessed`;
			if (pensionPlan?.enrolledDate) {
				status = "Enrolled";
				date = pensionPlan?.enrolledDate;
			} else if (pensionPlan?.optOutDate) {
				status = "Opted Out";
				date = pensionPlan?.optOutDate;
			} else if (pensionPlan?.optInDate) {
				status = "Opted In";
				date = pensionPlan?.optInDate;
			} else if (pensionPlan?.joinDate) {
				status = "Joined";
				date = pensionPlan?.joinDate;
			} else if (pensionPlan) {
				status = "Assessed";
			}
			if (!pensionPlan && enrolmentDeferredUntil) status = "Postponed";

			// const status = enrolmentDeferredUntil
			// 	? "Postponed"
			// 	: statusCode?.charAt(0) + statusCode?.slice(1).toLowerCase() || null;

			const employeeContribution = pensionPlan?.employeeContribution;
			const employerContribution = pensionPlan?.employerContribution;

			if (pensionPlan) {
				pensionPlan.agreement = employeePensionPlan?.agreement;
				pensionPlan.occupation = employeePensionPlan?.occupation;
			}

			return {
				...e,
				name: `${e.employeeName?.trim()} ${e.employeeSurname?.trim()}`,
				status,
				group: (employeePensionPlan?.customPensionContribution && { name: "Custom", id: "custom" }) || group,
				employeeContribution,
				employerContribution,
				pensionPlan,
				date: new Date(date),
			};
		});

	const pensionIntegrations = Object.values({ ...state?.integration?.pension }).map(({ name, canSyncUp }) => ({
		value: name,
		label: name,
		isDisabled: !canSyncUp,
		statusMessage: !canSyncUp ? (
			<div>
				{name} does not support creation of schemes outside of their product. To import a scheme, please use
				the&nbsp;
				<Link to="/organisation/settings/integrations">import feature</Link>
			</div>
		) : undefined,
	}));
	return {
		employees,
		groupOptions,
		statusOptions,
		pensionIntegrations,
		currency: state.company?.settings?.currency,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getEmployeePensions: () => dispatch(PENSIONACTIONS.getEmployeePensions()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeePensions);
